import {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {buildQueryParams} from '@/utils/commons';
import {
  getProductCompletion,
  ensureProductDisplayDefaults,
  extractImageUrls,
  mapUrlsToImageSlots,
  buildCompletionWarningMessage,
  shouldDisableEnAutofill
} from '@/utils/rewardsDisplay';
import {getProductList} from '@/services/ProductsService/product';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';

/**
 * Hook to manage product display state and operations
 */
export const useRewardsDisplayProducts = ({
  dispatch,
  formData,
  handleFormChange,
  languages,
  currentStep,
  enFallbackDescription
}) => {
  const display = useMemo(
    () => formData?.display || {productDisplay: {}},
    [formData?.display]
  );

  // Ref always holds latest display so async updates (e.g. useDefaultImage, image upload)
  // merge into current state and do not overwrite recent user input (e.g. reward text).
  const displayRef = useRef(display);
  displayRef.current = display;

  const effectiveProducts = useMemo(() => {
    // For PEP_VOUCHER_PRODUCT, use selectedPepVouchersProducts; otherwise use selectedProducts
    const isPepVoucherProduct =
      formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
    const selected = isPepVoucherProduct
      ? formData?.selectedPepVouchersProducts || []
      : formData?.selectedProducts || [];
    return selected.length > 0 ? selected : [];
  }, [
    formData?.rewardType,
    formData?.selectedProducts,
    formData?.selectedPepVouchersProducts
  ]);

  const [activeProductId, setActiveProductId] = useState(
    effectiveProducts?.[0]?.value || ''
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Sync active product when products change
  useEffect(() => {
    if (!activeProductId && effectiveProducts?.length > 0) {
      setActiveProductId(effectiveProducts[0].value);
    } else if (activeProductId && effectiveProducts?.length === 0) {
      setActiveProductId('');
    }
  }, [effectiveProducts, activeProductId]);

  // Product options with completion status
  const productOptions = useMemo(
    () =>
      (effectiveProducts || []).map((p) => {
        const id = p.value;
        const pd = display?.productDisplay?.[id];
        const {completed} = getProductCompletion(
          formData?.rewardType,
          pd,
          languages,
          formData?.rewardText
        );
        return {id, displayText: p.label, isBadge: false, completed};
      }),
    [
      effectiveProducts,
      display?.productDisplay,
      languages,
      formData?.rewardType,
      formData?.rewardText
    ]
  );

  const productChildList = useMemo(
    () =>
      productOptions.map((o) => ({
        id: o.id,
        displayText: o.displayText,
        leadingIcon: {icon: o.completed ? 'check_circle' : 'circle'}
      })),
    [productOptions]
  );

  const completedCount = useMemo(() => {
    const ids = (effectiveProducts || []).map((p) => p.value);
    let count = 0;
    ids.forEach((id) => {
      const pd = display?.productDisplay?.[id];
      const {completed} = getProductCompletion(
        formData?.rewardType,
        pd,
        languages,
        formData?.rewardText
      );
      if (completed) count += 1;
    });
    return {count, total: ids.length};
  }, [
    effectiveProducts,
    display,
    languages,
    formData?.rewardType,
    formData?.rewardText
  ]);

  // Toast notification helper
  const showToastNotification = useCallback(
    (message, type = 'error') => {
      dispatch(setToastNotifications({show: true, message, type}));
    },
    [dispatch]
  );

  // Update display for a specific product (reads latest display from ref to avoid
  // stale closure overwriting state when async updates run after user input).
  const updateDisplayForId = useCallback(
    (productId, update) => {
      const latestDisplay = displayRef.current ?? {productDisplay: {}};
      const current = latestDisplay.productDisplay?.[productId] || {};
      const nextEntry = {...current, ...update};
      const same =
        Object.keys(nextEntry).length === Object.keys(current).length &&
        Object.keys(nextEntry).every((k) => nextEntry[k] === current[k]);
      if (same) return;

      handleFormChange('display', {
        ...latestDisplay,
        productDisplay: {
          ...latestDisplay.productDisplay,
          [productId]: nextEntry
        }
      });
    },
    [handleFormChange]
  );

  const updateProductDisplay = useCallback(
    (update) => {
      if (!activeProductId) return;
      updateDisplayForId(activeProductId, update);
    },
    [activeProductId, updateDisplayForId]
  );

  const updateProductDisplayFor = useCallback(
    (productId, update) => updateDisplayForId(productId, update),
    [updateDisplayForId]
  );

  // Ensure EN defaults for a product
  const ensureEnglishDefaults = useCallback(
    (productId) => {
      const pd = display?.productDisplay?.[productId] || {};
      if (pd.enAutofillDisabled) return;

      const currentText = pd.rewardText || {};
      const currentEn = currentText.en || {};
      const hasEnTitle =
        typeof currentEn.title === 'string' &&
        currentEn.title.trim().length > 0;
      const hasEnDesc =
        typeof currentEn.description === 'string' &&
        currentEn.description.trim().length > 0;

      const product = (effectiveProducts || []).find(
        (p) => p.value === productId
      );
      const defaultTitle = product?.label || '';
      const defaultDescription = enFallbackDescription;

      const nextEn = {...currentEn};
      let changed = false;

      if (!hasEnTitle && defaultTitle) {
        nextEn.title = defaultTitle;
        changed = true;
      }
      if (!hasEnDesc && defaultDescription) {
        nextEn.description = defaultDescription;
        changed = true;
      }
      if (!changed) return;

      updateProductDisplayFor(productId, {
        rewardText: {...currentText, en: nextEn}
      });
    },
    [
      display?.productDisplay,
      effectiveProducts,
      enFallbackDescription,
      updateProductDisplayFor
    ]
  );

  // Fetch default images for a product
  const fetchInFlightRef = useRef(new Set());
  const fetchAndSetDefaultImages = useCallback(
    async (productId) => {
      if (fetchInFlightRef.current.has(productId)) return undefined;
      fetchInFlightRef.current.add(productId);

      try {
        const qp = buildQueryParams({pageSize: 1, page: 0});
        const filters = [
          {field: 'productId', type: 'string', value: String(productId)}
        ];
        const resp = await getProductList(qp, filters);
        const data = resp?.data ?? [];
        const items = Array.isArray(data) ? data : (data?.items ?? []);
        const item = items?.[0] || null;

        if (!item) return false;

        const urls = extractImageUrls(item);
        if (!urls?.length) {
          showToastNotification(
            'Default images are not available for this product.',
            'warning'
          );
          return false;
        }

        updateProductDisplayFor(productId, {
          images: mapUrlsToImageSlots(urls),
          useDefaultImage: true
        });

        return true;
      } catch {
        showToastNotification('Failed to fetch default product images.');

        return false;
      } finally {
        fetchInFlightRef.current.delete(productId);
      }
    },
    [updateProductDisplayFor, showToastNotification]
  );

  // Initialize product display entry
  useEffect(() => {
    if (currentStep !== 2 || !activeProductId) return;

    const pd = display?.productDisplay?.[activeProductId];
    if (!pd) {
      const ensured = ensureProductDisplayDefaults(
        activeProductId,
        display,
        effectiveProducts,
        undefined,
        enFallbackDescription
      );
      const currentEnsured = display?.productDisplay?.[activeProductId];
      const isSame =
        currentEnsured &&
        JSON.stringify(currentEnsured) === JSON.stringify(ensured);
      if (!isSame) {
        handleFormChange('display', {
          ...display,
          productDisplay: {
            ...display.productDisplay,
            [activeProductId]: ensured
          }
        });
      }
      return;
    }
    ensureEnglishDefaults(activeProductId);
  }, [
    currentStep,
    activeProductId,
    display,
    handleFormChange,
    effectiveProducts,
    enFallbackDescription,
    ensureEnglishDefaults
  ]);

  // Seed per-product rewardText with null for all language codes not yet present
  useEffect(() => {
    if (currentStep !== 2 || !activeProductId || languages.length === 0) return;
    const isBonusProducts =
      formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
    if (!isBonusProducts) return;

    const pd = display?.productDisplay?.[activeProductId];
    if (!pd) return;

    const currentText = pd.rewardText || {};
    let changed = false;
    const seeded = {...currentText};
    languages.forEach((lang) => {
      if (!(lang.code in seeded)) {
        seeded[lang.code] = null;
        changed = true;
      }
    });
    if (changed) {
      updateProductDisplayFor(activeProductId, {rewardText: seeded});
    }
  }, [
    currentStep,
    activeProductId,
    languages,
    formData?.rewardType,
    display?.productDisplay,
    updateProductDisplayFor
  ]);

  // Product switch handler
  const onProductChange = useCallback(
    (value) => {
      const nextId = value?.[0]?.id;
      const current = display.productDisplay[activeProductId];

      if (activeProductId && current) {
        const message = buildCompletionWarningMessage(
          display,
          effectiveProducts,
          languages,
          formData?.rewardType,
          formData?.rewardText
        );
        if (message) {
          setToastMessage(message);
          setShowToast(true);
        }
      }

      setActiveProductId(nextId);
      if (nextId) ensureEnglishDefaults(nextId);
    },
    [
      display,
      activeProductId,
      effectiveProducts,
      formData?.rewardType,
      formData?.rewardText,
      languages,
      ensureEnglishDefaults
    ]
  );

  // Update reward text for active product
  const updateRewardText = useCallback(
    (language, patch) => {
      const current = display.productDisplay[activeProductId] || {};
      const currentText = current.rewardText || {};
      const nextRewardText = {
        ...currentText,
        [language]: {...currentText?.[language], ...patch}
      };
      const disableAutofill = shouldDisableEnAutofill(language, patch);

      updateProductDisplay({
        rewardText: nextRewardText,
        ...(disableAutofill ? {enAutofillDisabled: true} : {})
      });
    },
    [display.productDisplay, activeProductId, updateProductDisplay]
  );

  return {
    display,
    effectiveProducts,
    productOptions,
    productChildList,
    completedCount,
    activeProductId,
    setActiveProductId,
    updateProductDisplay,
    updateProductDisplayFor,
    onProductChange,
    updateRewardText,
    showToast,
    setShowToast,
    toastMessage,
    showToastNotification,
    fetchAndSetDefaultImages,
    ensureEnglishDefaults
  };
};
